import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { createCheckout, fmtEuro, cartPricing, getProducts } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";

const CartSheet = () => {
  const { items, updateQty, updateDetail, removeItem, addItem, total, open, setOpen } = useCart();
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [displayQty, setDisplayQty] = useState(0);
  const { data: catalog } = useQuery({ queryKey: ["catalog"], queryFn: getProducts });
  const inCart = new Set(items.map((i) => i.lookup_key));
  const suggestions = (catalog?.products || []).filter((p) => !inCart.has(p.lookup_key));

  const label = (item) => t.products.items[item.id]?.name || item.id;

  const missing = items.some((i) => (i.fields || []).some((f) => !(i.details?.[f] || "").trim()));
  const pricing = cartPricing(items);
  const DISPLAY_PRICE = 4.99;
  const grandTotal = total + displayQty * DISPLAY_PRICE;

  const checkout = async () => {
    setLoading(true);
    const win = window.open("", "_blank");
    try {
      const { checkout_url } = await createCheckout({
        items: [
          ...items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity, details: i.details || null })),
          ...(displayQty > 0 ? [{ lookup_key: "display_stand", quantity: displayQty }] : []),
        ],
        origin_url: window.location.origin,
        locale: lang,
        pharmacy_name: items[0]?.details?.website || "",
      });
      if (win) win.location.href = checkout_url;
      else window.location.href = checkout_url;
    } catch (e) {
      if (win) win.close();
      toast.error(t.contact.error);
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col sm:max-w-md" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl text-primary">{t.cart.title}</SheetTitle>
          <SheetDescription className="sr-only">{t.cart.title}</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground" data-testid="cart-empty">
            {t.cart.empty}
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-4 overflow-y-auto py-4">
              {items.map((item) => (
                <div key={item.lookup_key} data-testid={`cart-item-${item.id}`}
                  className="rounded-lg border border-border p-3">
                  <div className="flex gap-3">
                    {item.image && <img src={item.image} alt="" className="h-16 w-16 rounded-md object-cover" />}
                    <div className="flex flex-1 flex-col">
                      <span className="text-sm font-semibold text-primary leading-tight">{label(item)}</span>
                      <span className="text-sm text-accent font-medium">{fmtEuro(item.price)}{item.interval ? t.plans.month : ""}</span>
                      <div className="mt-auto flex items-center gap-2 pt-2">
                        <button onClick={() => updateQty(item.lookup_key, -1)} data-testid={`cart-minus-${item.id}`}
                          className="grid h-7 w-7 place-items-center rounded-full border border-border hover:border-accent">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
                        <button onClick={() => updateQty(item.lookup_key, 1)} data-testid={`cart-plus-${item.id}`}
                          className="grid h-7 w-7 place-items-center rounded-full border border-border hover:border-accent">
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => removeItem(item.lookup_key)} data-testid={`cart-remove-${item.id}`}
                          className="ml-auto text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {(item.fields || []).length > 0 && (
                    <div className="mt-3 space-y-2">
                      {item.fields.map((f) => (
                        <div key={f}>
                          <label className="mb-1 block text-xs font-medium text-secondary">
                            {t.cart.fields[f]} <span className="text-destructive">*</span>
                          </label>
                          <Input value={item.details?.[f] || ""}
                            onChange={(e) => updateDetail(item.lookup_key, f, e.target.value)}
                            data-testid={`cart-field-${item.id}-${f}`}
                            className="h-9 text-sm" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Display add-on (aantal kiesbaar) */}
              <div className="rounded-lg border border-border p-3" data-testid="cart-display-addon">
                <div className="flex items-center gap-3">
                  <img src="/display.jpg" alt={t.cart.display.title} className="h-16 w-16 rounded-md object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-tight text-primary">{t.cart.display.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t.cart.display.desc}</p>
                    <p className="mt-0.5 text-sm font-medium text-accent">{fmtEuro(DISPLAY_PRICE)} {t.cart.display.perPiece}</p>
                  </div>
                  {displayQty === 0 ? (
                    <button onClick={() => setDisplayQty(1)} data-testid="cart-display-add"
                      className="flex shrink-0 items-center gap-1 rounded-full border border-accent px-3 py-1.5 text-xs font-semibold text-accent transition-colors hover:bg-accent hover:text-white">
                      <Plus className="h-3.5 w-3.5" /> {t.cart.display.add}
                    </button>
                  ) : (
                    <div className="flex shrink-0 items-center gap-2">
                      <button onClick={() => setDisplayQty((q) => Math.max(0, q - 1))} data-testid="cart-display-minus"
                        className="grid h-7 w-7 place-items-center rounded-full border border-border hover:border-accent">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-semibold" data-testid="cart-display-qty">{displayQty}</span>
                      <button onClick={() => setDisplayQty((q) => q + 1)} data-testid="cart-display-plus"
                        className="grid h-7 w-7 place-items-center rounded-full border border-border hover:border-accent">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {suggestions.length > 0 && (
                <div className="pt-2">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t.cart.addMore}</p>
                  <div className="space-y-2">
                    {suggestions.map((p) => (
                      <div key={p.id} data-testid={`cart-suggestion-${p.id}`}
                        className="flex items-center gap-3 rounded-lg border border-dashed border-border p-2">
                        {p.image && <img src={p.image} alt="" className="h-10 w-10 rounded-md object-cover" />}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-primary">{t.products.items[p.id]?.name || p.id}</p>
                          <p className="text-xs font-medium text-accent">{fmtEuro(p.price)}</p>
                        </div>
                        <button onClick={() => addItem(p)} data-testid={`cart-add-${p.id}`}
                          className="flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-accent">
                          <Plus className="h-3.5 w-3.5" /> {t.products.add}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              {pricing.individualDiscounted < pricing.individualFull && (
                <div className="flex items-center justify-between text-sm" data-testid="cart-volume-discount">
                  <span className="font-medium text-secondary">Volumekorting ({pricing.individualQty} pods)</span>
                  <span>
                    <span className="mr-2 text-muted-foreground line-through">{fmtEuro(pricing.individualFull)}</span>
                    <span className="font-semibold text-secondary">{fmtEuro(pricing.individualDiscounted)}</span>
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t.cart.total}</span>
                <span data-testid="cart-total" className="font-heading text-2xl font-bold text-primary">{fmtEuro(grandTotal)}</span>
              </div>
              {missing && (
                <p className="text-xs font-medium text-destructive" data-testid="cart-fill-required">{t.cart.fillRequired}</p>
              )}
              <button onClick={checkout} disabled={loading || missing} data-testid="checkout-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-accent py-3.5 font-semibold text-white transition-transform hover:bg-secondary active:scale-95 disabled:cursor-not-allowed disabled:opacity-60">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" />{t.cart.processing}</> : t.cart.checkout}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
